import { useState } from 'react';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { EmailMonitor } from '../../../../../types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  getEmailMonitors,
  getUserQuery,
} from '../../../../api/users-api';
import DeleteEmailMonitorDialog from './delete-monitor-dialog';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useParams } from 'react-router-dom';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { Messages } from '../../../../utils/message-constants';
import EmailMonitorDialog from './email-monitor-dialog';
import dayjs from 'dayjs';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import CreateButton from '../../../../shared/buttons/create-button';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import Edit from '@mui/icons-material/Edit';

export default function AuditGrid() {
  const { userEmail } = useParams() as { userEmail: string };
  const { userRoles, changeLogRoles } = useUserSettings();

  const { data: user } = useQuery(getUserQuery(userEmail));

  const queryClient = useQueryClient();
  const [openDeleteMonitors, setOpenDeleteMonitors] = useState(false);
  const [openAddEmailMonitor, setOpenAddEmailMonitor] = useState(false);
  const [openEditEmailMonitor, setOpenEditEmailMonitor] = useState(false);
  const [selectedMonitor, setSelectedMonitor] = useState<EmailMonitor>();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [UserKeys.EmailMonitors, userEmail],
    queryFn: () => getEmailMonitors(userEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('email monitors'),
    },
  });

  const columns: GridColDef<EmailMonitor>[] = [
    {
      field: 'listeningUser',
      headerName: 'Listening User',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
    },
    {
      field: 'monitoredUser',
      headerName: 'Monitored User',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => {
        const date = dayjs()
          .year(params.row.startYear)
          .month(params.row.startMonth)
          .date(params.row.startDay);
        return date.toDate().toLocaleDateString();
      },
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => {
        const date = dayjs()
          .year(params.row.endYear)
          .month(params.row.endMonth)
          .date(params.row.endDay);
        return date.toDate().toLocaleDateString();
      },
    },
    {
      field: 'draftMonitoring',
      headerName: 'Drafts Monitor',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => {
        if (params.value === 'NONE') {
          return 'Do not monitor drafts';
        } else if (params.value === 'HEADER_ONLY') {
          return 'Draft Headers Only';
        } else return 'Full drafts monitoring';
      },
    },
    {
      field: 'chatMonitoring',
      headerName: 'Chats Monitor',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => {
        if (params.value === 'NONE') {
          return 'Do not monitor drafts';
        } else if (params.value === 'HEADER_ONLY') {
          return 'Chat Headers Only';
        } else return 'Full chats monitoring';
      },
    },
    {
      field: 'fullOutgoing',
      headerName: 'Outgoing Messages',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => {
        if (params.value === 'FULL_MESSAGE') return 'Full Messages';
        else return 'Headers Only';
      },
    },
    {
      field: 'fullIncoming',
      headerName: 'Incoming Messages',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => {
        if (params.value === 'FULL_MESSAGE') return 'Full Messages';
        else return 'Headers Only';
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 80,
      getActions: (params) => {
        const buttonArray = [];
        if (
          isAbleToSeeUIElement(userRoles, UserRoles.EditEmailMonitors.value)
        ) {
          buttonArray.push(
            <GridActionsCellItem
              label="Edit"
              icon={
                <Tooltip arrow title="Edit">
                  <Edit />
                </Tooltip>
              }
              onClick={() => {
                setSelectedMonitor(params.row);
                setOpenEditEmailMonitor(true);
              }}
            />
          );
        }
        return buttonArray;
      },
    },
  ];

  if (!user) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', minHeight: 300 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        noFilter
        noQuickFilter
        primaryButtons={
          <CreateButton
            onClick={() => {
              setOpenAddEmailMonitor(true);
            }}
            disabled={
              !isAbleToSeeUIElement(userRoles, UserRoles.AddEmailMonitors.value)
            }
            label="Add Monitor"
          />
        }
        secondaryButtons={
          <>
            <SharedIconButton
              variant="delete"
              onClick={() => {
                setOpenDeleteMonitors(true);
              }}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  userRoles || [],
                  UserRoles.RemoveEmailMonitors.value
                )
              }
              tooltipLabel={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.RemoveEmailMonitors.value
                )
                  ? `You are missing the ${UserRoles.RemoveEmailMonitors.name} permission to perform this action.`
                  : 'Delete'
              }
            />
            <SharedIconButton
              variant="refresh"
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: [UserKeys.EmailMonitors, userEmail],
                });
              }}
              tooltipLabel="Refresh"
            />
          </>
        }
      />
    );
  }

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Email Monitors', <AdminPanelSettings />),
        }}
        getRowId={(row) => row.listeningUser}
        rows={data || []}
        columns={columns}
        loading={isLoading || isFetching}
        disableColumnSelector
        disableColumnMenu
        checkboxSelection
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />
      {selectedRows.length > 0 && openDeleteMonitors ? (
        <DeleteEmailMonitorDialog
          open={openDeleteMonitors}
          handleClose={() => setOpenDeleteMonitors(false)}
          setSelectedMonitors={setSelectedRows}
          selectedMonitors={selectedRows}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {openAddEmailMonitor ? (
        <EmailMonitorDialog
          dialogType="create"
          selectedEmailMonitor={null}
          open={openAddEmailMonitor}
          handleClose={() => setOpenAddEmailMonitor(false)}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {openEditEmailMonitor && selectedMonitor ? (
        <EmailMonitorDialog
          dialogType="edit"
          selectedEmailMonitor={selectedMonitor}
          open={openEditEmailMonitor}
          handleClose={() => setOpenEditEmailMonitor(false)}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
    </>
  );
}
